<template>
  <b-card class="okr-weekly-tracking">
    <div class="d-flex align-items-center justify-content-between flex-wrap mb-1" style="gap: 0.5rem">
      <span class="font-weight-bolder">Acompanhamento semanal</span>
      <div class="d-flex align-items-center" style="gap: 0.5rem">
        <b-form-select
          v-if="weeks.length"
          v-model="selectedWeekIndex"
          :options="weekOptions"
          size="sm"
          style="max-width: 220px"
        />
        <b-button
          v-if="weeks.length"
          size="sm"
          :variant="showConstancy ? 'primary' : 'outline-primary'"
          class="constancy-toggle"
          :title="showConstancy ? 'Ocultar tabela de constância' : 'Exibir tabela de constância'"
          @click="showConstancy = !showConstancy"
        >
          <feather-icon :icon="showConstancy ? 'MinusIcon' : 'PlusIcon'" size="14" />
          <span class="ml-25 d-none d-sm-inline">Constância</span>
        </b-button>
      </div>
    </div>

    <!-- Skeleton loader -->
    <div v-if="loading" class="skeleton-wrap">
      <div class="skeleton-row header" />
      <div v-for="n in 5" :key="n" class="skeleton-row" :style="{ animationDelay: `${n * 60}ms` }" />
    </div>

    <template v-else-if="selectedWeek">
      <b-alert v-if="recalibrated" show variant="warning" class="font-small-3 py-50 px-75 mb-1">
        Meta recalibrada — semanas anteriores não atingidas foram redistribuídas.
      </b-alert>

      <div class="week-meta mb-75">
        <span class="text-muted font-small-3">
          {{ selectedWeek.start_date | formatDate }} — {{ selectedWeek.end_date | formatDate }}
        </span>
        <b-badge v-if="selectedWeek.is_current" variant="light-primary" class="ml-50 font-small-2">
          Esta semana
        </b-badge>
        <b-badge v-else-if="!selectedWeek.is_past && !selectedWeek.is_current" variant="light-secondary" class="ml-50 font-small-2">
          Futura
        </b-badge>
        <span class="text-muted font-small-2 ml-auto">
          Acumulado até a semana S{{ selectedWeek.week_number }}
        </span>
      </div>

      <div v-if="activeMetrics.length" class="metrics-tiles">
        <div v-for="metric in activeMetrics" :key="metric.key" class="metric-tile">
          <div class="metric-label">{{ metric.label }}</div>
          <div class="metric-values">
            <span class="metric-realized">{{ cumulativeRealized(metric.key) }}</span>
            <span class="text-muted font-small-3">/ {{ cumulativePlanned(metric.key) }}</span>
          </div>
          <div class="text-muted font-small-2 mb-25">Realizado / Meta acumulada</div>
          <div class="d-flex justify-content-between align-items-center mb-25">
            <span :class="diffClass(metric.key)" class="font-small-3">{{ diffLabel(metric.key) }}</span>
            <span class="pct-badge" :class="pctClass(metric.key)">{{ metricPct(metric.key) }}%</span>
          </div>
          <b-progress
            :value="metricPctCapped(metric.key)"
            :max="100"
            height="4px"
            :variant="metricPct(metric.key) >= 100 ? 'success' : metricPct(metric.key) >= 60 ? 'primary' : 'warning'"
          />
        </div>
      </div>

      <!-- Sem OKR registrado / sem metas: estado vazio amigável -->
      <div v-else class="text-center text-muted font-small-3 py-2">
        <feather-icon icon="ClipboardIcon" size="20" class="mb-75 d-block mx-auto" />
        Registre seu OKR para acompanhar as metas semanais acumuladas.
      </div>

      <!-- Overall progress -->
      <div v-if="activeMetrics.length" class="overall-progress mt-1">
        <div class="d-flex justify-content-between font-small-2 mb-25">
          <span class="text-muted">Progresso geral (acumulado)</span>
          <span class="font-weight-bolder">{{ overallPct }}%</span>
        </div>
        <b-progress
          :value="overallPct"
          :max="100"
          height="5px"
          :variant="overallPct >= 100 ? 'success' : overallPct >= 60 ? 'primary' : 'warning'"
        />
      </div>

      <div class="action-plan-block mt-1">
        <label class="font-weight-bolder font-small-3 mb-25 d-block">
          Plano de ação — S{{ selectedWeek.week_number }}
        </label>
        <b-form-textarea
          v-model="actionPlanDraft"
          size="sm"
          rows="2"
          no-resize
          class="action-plan-textarea"
          placeholder="Plano de ação da semana..."
          :disabled="readOnly"
          @blur="saveActionPlan"
        />
        <small v-if="actionPlanSaved" class="text-success font-small-2">Plano de ação salvo.</small>
      </div>

      <b-collapse v-model="showConstancy">
        <div class="constancy-wrapper mt-1">
          <OkrConstancyTable
            :consultant-id="consultantId"
            :year="year"
            :quarter="quarter"
          />
        </div>
      </b-collapse>
    </template>

    <div v-else-if="!loading && !weeks.length" class="text-muted font-small-3 py-2 text-center">
      Nenhum dado de acompanhamento disponível.
    </div>
  </b-card>
</template>

<script>
import {
  BCard, BFormSelect, BAlert, BBadge, BProgress, BButton, BCollapse, BFormTextarea,
} from 'bootstrap-vue'
import FeatherIcon from '@core/components/feather-icon/FeatherIcon.vue'
import { mapActions } from 'vuex'
import * as types from '../store/types'
import OkrConstancyTable from './OkrConstancyTable.vue'

export default {
  name: 'OkrWeeklyTracking',
  components: {
    BCard, BFormSelect, BAlert, BBadge, BProgress, BButton, BCollapse, BFormTextarea, FeatherIcon, OkrConstancyTable,
  },

  props: {
    consultantId: { type: Number, required: true },
    year:         { type: Number, required: true },
    quarter:      { type: Number, required: true },
    readOnly:     { type: Boolean, default: false },
  },

  data() {
    return {
      loading:           false,
      recalibrated:      false,
      weeks:             [],
      selectedWeekIndex: 0,
      showConstancy:     false,
      actionPlanDraft:   '',
      actionPlanSaved:   false,
      metrics: [
        { key: 'hot_leads',       label: 'Leads quentes' },
        { key: 'ea',              label: 'EA' },
        { key: 'ef',              label: 'EF' },
        { key: 'ep',              label: 'EP' },
        { key: 'recommendations', label: 'Recomendações' },
      ],
    }
  },

  computed: {
    selectedWeek() {
      return this.weeks[this.selectedWeekIndex] ?? null
    },

    weekOptions() {
      return this.weeks.map((w, i) => ({
        value: i,
        text:  `S${w.week_number} — ${w.start_date} a ${w.end_date}${w.is_current ? ' (atual)' : ''}`,
      }))
    },

    activeMetrics() {
      const week = this.selectedWeek
      if (!week) return this.metrics
      return this.metrics.filter(m => {
        const planned  = Number(week.cumulative_planned?.[m.key] ?? 0)
        const realized = Number(week.cumulative_realized?.[m.key] ?? 0)
        return planned > 0 || realized > 0
      })
    },

    overallPct() {
      const week = this.selectedWeek
      if (!week) return 0
      let totalPlanned = 0
      let totalRealized = 0
      for (const m of this.activeMetrics) {
        totalPlanned  += Number(week.cumulative_planned?.[m.key] ?? 0)
        totalRealized += Number(week.cumulative_realized?.[m.key] ?? 0)
      }
      if (totalPlanned === 0) return 0
      return Math.min(100, Math.round((totalRealized / totalPlanned) * 100))
    },
  },

  watch: {
    consultantId: 'load',
    year:         'load',
    quarter:      'load',
    selectedWeekIndex() {
      this.syncActionPlanDraft()
    },
  },

  mounted() {
    this.load()
  },

  methods: {
    ...mapActions({
      fetch:                types.GET_WEEKLY_TRACKING,
      saveActionPlanAction: types.SAVE_ACTION_PLAN,
    }),

    async load() {
      if (!this.year || !this.quarter || !this.consultantId) return
      this.loading = true
      try {
        const resp = await this.fetch({
          consultant_id: this.consultantId,
          year:          this.year,
          quarter:       this.quarter,
        })
        this.recalibrated = resp.data.recalibrated
        this.weeks        = resp.data.weeks
        const current = this.weeks.findIndex(w => w.is_current)
        this.selectedWeekIndex = current >= 0 ? current : 0
        this.syncActionPlanDraft()
      } finally {
        this.loading = false
      }
    },

    syncActionPlanDraft() {
      this.actionPlanDraft = this.selectedWeek?.action_plan ?? ''
      this.actionPlanSaved = false
    },

    async saveActionPlan() {
      if (this.readOnly || !this.selectedWeek) return
      const week = this.selectedWeek
      if ((week.action_plan ?? '') === this.actionPlanDraft) return
      await this.saveActionPlanAction({
        consultant_id: this.consultantId,
        year:          this.year,
        quarter:       this.quarter,
        week_number:   week.week_number,
        action_plan:   this.actionPlanDraft ?? '',
      })
      week.action_plan     = this.actionPlanDraft
      this.actionPlanSaved = true
    },

    cumulativePlanned(key) {
      return Number(this.selectedWeek?.cumulative_planned?.[key] ?? 0)
    },

    cumulativeRealized(key) {
      return Number(this.selectedWeek?.cumulative_realized?.[key] ?? 0)
    },

    diffValue(key) {
      return this.cumulativeRealized(key) - this.cumulativePlanned(key)
    },

    diffLabel(key) {
      const diff = this.diffValue(key)
      if (diff < 0) return `Faltam ${Math.abs(diff)}`
      if (diff > 0) return `+${diff} acima`
      return 'Em dia'
    },

    diffClass(key) {
      const diff = this.diffValue(key)
      if (diff < 0) return 'text-danger font-weight-bolder'
      if (diff > 0) return 'text-success font-weight-bolder'
      return 'text-muted'
    },

    metricPct(key) {
      const planned  = this.cumulativePlanned(key)
      const realized = this.cumulativeRealized(key)
      if (planned === 0) return realized > 0 ? 100 : 0
      return Math.round((realized / planned) * 100)
    },

    metricPctCapped(key) {
      return Math.min(this.metricPct(key), 100)
    },

    pctClass(key) {
      const pct = this.metricPct(key)
      if (pct >= 100) return 'pct-success'
      if (pct >= 60)  return 'pct-warning'
      return 'pct-danger'
    },
  },
}
</script>

<style scoped>
.week-meta {
  display: flex;
  align-items: center;
}
.metrics-tiles {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(170px, 1fr));
  gap: 0.75rem;
}
.metric-tile {
  border: 1px solid #ebe9f1;
  border-radius: 6px;
  padding: 0.6rem 0.75rem;
}
.metric-label {
  font-size: 0.72rem;
  font-weight: 700;
  color: #6e6b7b;
  text-transform: uppercase;
  letter-spacing: 0.03em;
}
.metric-values {
  display: flex;
  align-items: baseline;
  gap: 0.3rem;
}
.metric-realized {
  font-size: 1.35rem;
  font-weight: 700;
  line-height: 1.3;
}
.action-plan-textarea {
  overflow-y: auto;
}
.pct-badge {
  font-size: 0.75rem;
  font-weight: 700;
  border-radius: 4px;
  padding: 1px 5px;
}
.pct-success { background: rgba(40,199,111,0.12); color: #28c76f; }
.pct-warning { background: rgba(255,159,67,0.12);  color: #ff9f43; }
.pct-danger  { background: rgba(234,84,85,0.1);    color: #ea5455; }

.constancy-toggle {
  display: inline-flex;
  align-items: center;
  padding: 0.35rem 0.6rem;
}
.action-plan-block {
  border-top: 1px solid #ebe9f1;
  padding-top: 0.75rem;
}
.constancy-wrapper {
  border-top: 1px solid #ebe9f1;
  padding-top: 0.75rem;
}

/* Skeleton loader */
.skeleton-wrap {
  display: flex;
  flex-direction: column;
  gap: 0.4rem;
}
.skeleton-row {
  height: 20px;
  border-radius: 4px;
  background: linear-gradient(90deg, #f0f0f0 25%, #e0e0e0 50%, #f0f0f0 75%);
  background-size: 200% 100%;
  animation: shimmer 1.2s infinite ease-in-out both;
}
.skeleton-row.header {
  width: 60%;
  height: 14px;
  margin-bottom: 0.2rem;
}
@keyframes shimmer {
  0%   { background-position: 200% 0; }
  100% { background-position: -200% 0; }
}
.dark-layout .skeleton-row {
  background: linear-gradient(90deg, #2d3448 25%, #3b4253 50%, #2d3448 75%);
  background-size: 200% 100%;
}
.dark-layout .action-plan-block,
.dark-layout .constancy-wrapper {
  border-top-color: #3b4253;
}
.dark-layout .metric-tile {
  border-color: #3b4253;
}
</style>
